import { useEffect, useState } from "react";
import { apiClient } from "@/lib/api-client.js";
import { num, eur, pct } from "@/lib/format.js";

interface GeoBuilding {
  latitude?: number;
  roofSurfaces?: Array<{ roofType: string; tiltDeg?: number; azimuthDeg?: number }>;
}

// Ergebnis-Shapes (Spiegel der pv-analysis-Engine-Ausgaben).
interface QuickYield {
  specificYieldKwhPerKwp: number;
  poaAnnualKwhM2: number;
  performanceRatio: number;
  monthlySpecificYieldKwhPerKwp: number[];
  lossStackEfficiency: number;
  meanTemperatureDerate: number;
}
interface StringFinding { code?: string; message: string }
interface StringVerdict {
  ok: boolean;
  violations: StringFinding[];
  warnings: StringFinding[];
  stringVocColdV: number;
  stringVmpHotV: number;
  dcAcRatio: number;
}
interface BatteryEcon {
  dispatch: {
    selfConsumptionRate: number;
    selfSufficiencyRate: number;
    selfConsumedKwh: number;
    gridImportKwh: number;
    exportedKwh: number;
  };
  economics: {
    annualSavingsEur: number;
    npvEur: number;
    irr: number | null;
    paybackYears: number | null;
    realLcoeEurPerKwh: number;
    nominalLcoeEurPerKwh: number;
  };
}

const MONTHS = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "6px 8px",
  border: "1px solid var(--color-border, #cbd5e1)",
  borderRadius: 6,
  fontSize: 13,
  boxSizing: "border-box",
};
const card: React.CSSProperties = { marginBottom: 16, padding: 16 };
const grid3: React.CSSProperties = { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 };
const kpiBox: React.CSSProperties = {
  padding: "10px 14px",
  background: "var(--color-surface-2, #f8fafc)",
  borderRadius: 8,
  border: "1px solid var(--color-border, #e2e8f0)",
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "block", fontSize: 12, color: "var(--color-text-muted)" }}>
      {label}
      <div style={{ marginTop: 3 }}>{children}</div>
    </label>
  );
}
function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div style={kpiBox}>
      <div style={{ fontSize: 11, color: "var(--color-text-muted)" }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, marginTop: 2 }}>{value}</div>
    </div>
  );
}

/**
 * PV-Analyse — Oberfläche für die OSS-geharvesteten Engines (clean-room) über
 * /api/pv-analysis: Ertrag (Transposition + Verlust-Stack), Verschaltungs-Check
 * (MPPT/Vdcmax) und Speicher-Wirtschaftlichkeit (Eigenverbrauch + NPV/IRR/LCOE).
 */
export function PvAnalyseTab({ projectId }: { projectId?: string }) {
  // ── Ertrag ──
  const [y, setY] = useState<{ latitudeDeg: number; tiltDeg: number; azimuthDeg: number; transpositionModel: "haydavies" | "perez"; extraShadingPct: number }>({
    latitudeDeg: 47.5,
    tiltDeg: 30,
    azimuthDeg: 180,
    transpositionModel: "perez",
    extraShadingPct: 0,
  });
  const [yieldRes, setYieldRes] = useState<QuickYield | null>(null);
  const [yieldBusy, setYieldBusy] = useState(false);
  const [prefilled, setPrefilled] = useState(false);

  // ── Fernverschattung (Horizont) ──
  interface ObstacleRow { bearingDeg: number; distanceM: number; heightDeltaM: number }
  const [obstructions, setObstructions] = useState<ObstacleRow[]>([
    { bearingDeg: 180, distanceM: 15, heightDeltaM: 8 },
  ]);
  const [horizonRes, setHorizonRes] = useState<{ annualShadingLoss: number; skyViewFactor: number } | null>(null);

  const runHorizon = async () => {
    const res = await apiClient.post("/pv-analysis/horizon-derate", {
      latDeg: y.latitudeDeg,
      lngDeg: 13,
      tiltDeg: y.tiltDeg,
      azimuthDeg: y.azimuthDeg,
      obstructions,
    });
    if (res.success) {
      const d = (res.data as { data?: { annualShadingLoss: number; skyViewFactor: number } }).data;
      if (d) setHorizonRes(d);
    }
  };
  const applyHorizon = () => {
    if (horizonRes) setY((p) => ({ ...p, extraShadingPct: Math.round(horizonRes.annualShadingLoss * 1000) / 10 }));
  };

  // Ertragseingaben aus den echten Projektdaten vorbelegen (Standort + erste
  // Dachfläche), damit die Analyse das reale Gebäude widerspiegelt.
  useEffect(() => {
    if (!projectId) return;
    let alive = true;
    void (async () => {
      const res = await apiClient.get<{ buildings: GeoBuilding[] }>(`/projects/${projectId}/geospatial`);
      if (!alive || !res.success) return;
      const b = (res.data?.buildings ?? []).find((x) => x.latitude !== undefined);
      if (!b) return;
      const roof = (b.roofSurfaces ?? []).find((r) => r.roofType !== "obstacle");
      setY((p) => ({
        ...p,
        latitudeDeg: b.latitude ?? p.latitudeDeg,
        tiltDeg: roof?.tiltDeg ?? p.tiltDeg,
        azimuthDeg: roof?.azimuthDeg ?? p.azimuthDeg,
      }));
      setPrefilled(true);
    })();
    return () => { alive = false; };
  }, [projectId]);

  const runYield = async () => {
    setYieldBusy(true);
    const res = await apiClient.post("/pv-analysis/quick-yield", y);
    if (res.success) setYieldRes(res.data as QuickYield);
    setYieldBusy(false);
  };

  // ── Verschaltung ──
  const [s, setS] = useState({
    vocStc: 41, vmpStc: 34, betaVocPctPerK: -0.27, pmpStcW: 400,
    mpptMinV: 200, mpptMaxV: 800, vdcMaxV: 1000, mpptCount: 2, pacoW: 8000,
    modulesPerString: 18, stringsPerMppt: 1, siteMinTempC: -15, siteMaxCellTempC: 70,
  });
  const [strRes, setStrRes] = useState<StringVerdict | null>(null);
  const [strBusy, setStrBusy] = useState(false);
  const [suggestNote, setSuggestNote] = useState<string | null>(null);

  // ── Komponenten-Katalog (für Autofill aus echten Datenblättern, pvfree-ETL) ──
  interface CatComponent { id: string; manufacturer: string; modelName: string; specs: Record<string, unknown> }
  const [catModules, setCatModules] = useState<CatComponent[]>([]);
  const [catInverters, setCatInverters] = useState<CatComponent[]>([]);
  useEffect(() => {
    let alive = true;
    void (async () => {
      const [mods, invs] = await Promise.all([
        apiClient.get<CatComponent[]>("/components?type=pv_module"),
        apiClient.get<CatComponent[]>("/components?type=inverter"),
      ]);
      if (!alive) return;
      if (mods.success) setCatModules(mods.data ?? []);
      if (invs.success) setCatInverters(invs.data ?? []);
    })();
    return () => { alive = false; };
  }, []);

  const numSpec = (sp: Record<string, unknown>, k: string): number | undefined => {
    const v = sp[k];
    return typeof v === "number" ? v : undefined;
  };

  const pickModule = (id: string) => {
    const c = catModules.find((x) => x.id === id);
    if (!c) return;
    setS((p) => ({
      ...p,
      vocStc: numSpec(c.specs, "vocStc") ?? p.vocStc,
      vmpStc: numSpec(c.specs, "vmpStc") ?? p.vmpStc,
      betaVocPctPerK: numSpec(c.specs, "betaVocPctPerK") ?? p.betaVocPctPerK,
      pmpStcW: numSpec(c.specs, "pmpStcW") ?? p.pmpStcW,
    }));
  };
  const pickInverter = (id: string) => {
    const c = catInverters.find((x) => x.id === id);
    if (!c) return;
    setS((p) => ({
      ...p,
      mpptMinV: numSpec(c.specs, "mpptMinV") ?? p.mpptMinV,
      mpptMaxV: numSpec(c.specs, "mpptMaxV") ?? p.mpptMaxV,
      vdcMaxV: numSpec(c.specs, "vdcMaxV") ?? p.vdcMaxV,
      pacoW: numSpec(c.specs, "pacoW") ?? p.pacoW,
    }));
  };

  const runSuggest = async () => {
    const res = await apiClient.post("/pv-analysis/string-suggest", {
      module: { vocStc: s.vocStc, vmpStc: s.vmpStc, betaVocPctPerK: s.betaVocPctPerK },
      inverter: { mpptMinV: s.mpptMinV, mpptMaxV: s.mpptMaxV, vdcMaxV: s.vdcMaxV, mpptCount: s.mpptCount, pacoW: s.pacoW },
      siteMinTempC: s.siteMinTempC,
      siteMaxCellTempC: s.siteMaxCellTempC,
      stringsPerMppt: s.stringsPerMppt,
    });
    if (res.success) {
      const sug = res.data as { recommended: number | null; note: string };
      setSuggestNote(sug.note);
      if (sug.recommended) setS((p) => ({ ...p, modulesPerString: sug.recommended! }));
    }
  };

  // ── Datenblatt-Import (.PAN/.OND) ──
  interface ParsedSheet {
    kind: "pan" | "ond";
    module?: { manufacturer?: string; model?: string; pnomW?: number; vocV?: number; vmpV?: number };
    inverter?: { manufacturer?: string; model?: string; pacoW?: number; vMpptMinV?: number; vMpptMaxV?: number; vAbsMaxV?: number; mpptCount?: number };
    report?: { parsedKeys: string[]; missingKeys: string[] };
  }
  const [sheet, setSheet] = useState<ParsedSheet | null>(null);
  const [sheetName, setSheetName] = useState("");

  const onSheetFile = async (file: File | undefined) => {
    if (!file) return;
    setSheetName(file.name);
    const text = await file.text();
    const res = await apiClient.post("/pv-analysis/parse-datasheet", { text });
    if (res.success) setSheet(res.data as ParsedSheet);
  };

  const applySheet = () => {
    if (!sheet) return;
    if (sheet.kind === "pan" && sheet.module) {
      const m = sheet.module;
      setS((p) => ({ ...p, vocStc: m.vocV ?? p.vocStc, vmpStc: m.vmpV ?? p.vmpStc, pmpStcW: m.pnomW ?? p.pmpStcW }));
    } else if (sheet.kind === "ond" && sheet.inverter) {
      const inv = sheet.inverter;
      setS((p) => ({
        ...p,
        mpptMinV: inv.vMpptMinV ?? p.mpptMinV,
        mpptMaxV: inv.vMpptMaxV ?? p.mpptMaxV,
        vdcMaxV: inv.vAbsMaxV ?? p.vdcMaxV,
        mpptCount: inv.mpptCount ?? p.mpptCount,
        pacoW: inv.pacoW ?? p.pacoW,
      }));
    }
  };

  const runString = async () => {
    setStrBusy(true);
    const res = await apiClient.post("/pv-analysis/string-check", {
      module: { vocStc: s.vocStc, vmpStc: s.vmpStc, betaVocPctPerK: s.betaVocPctPerK, pmpStcW: s.pmpStcW },
      inverter: { mpptMinV: s.mpptMinV, mpptMaxV: s.mpptMaxV, vdcMaxV: s.vdcMaxV, mpptCount: s.mpptCount, pacoW: s.pacoW },
      modulesPerString: s.modulesPerString, stringsPerMppt: s.stringsPerMppt,
      siteMinTempC: s.siteMinTempC, siteMaxCellTempC: s.siteMaxCellTempC,
    });
    if (res.success) setStrRes(res.data as StringVerdict);
    setStrBusy(false);
  };

  // ── Speicher-Wirtschaftlichkeit ──
  const [b, setB] = useState({
    annualConsumptionKwh: 4500, annualYieldKwh: 9000, batteryUsableKwh: 10,
    capexEur: 18000, annualOpexEur: 150, electricityPriceEurPerKwh: 0.32,
    feedInTariffEurPerKwh: 0.07, discountRatePct: 3, years: 20,
  });
  const [batRes, setBatRes] = useState<BatteryEcon | null>(null);
  const [batBusy, setBatBusy] = useState(false);

  const runBattery = async () => {
    setBatBusy(true);
    const res = await apiClient.post("/pv-analysis/battery-economics", b);
    if (res.success) setBatRes(res.data as BatteryEcon);
    setBatBusy(false);
  };

  const numInput = <T extends object>(
    obj: T, set: (v: T) => void, key: keyof T,
  ) => (
    <input
      type="number"
      style={inputStyle}
      value={obj[key] as unknown as number}
      onChange={(e) => set({ ...obj, [key]: Number(e.target.value) })}
    />
  );

  const maxMonthly = yieldRes ? Math.max(...yieldRes.monthlySpecificYieldKwhPerKwp, 1) : 1;

  return (
    <div>
      {/* ── Ertragsanalyse ── */}
      <div className="card" style={card}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Ertragsanalyse</h3>
        <p style={{ fontSize: 12, color: "var(--color-text-muted)", marginBottom: 12 }}>
          Orientierungsabhängiger spezifischer Ertrag aus Strahlungs-Transposition (Hay-Davies),
          Zelltemperatur (Faiman) und PVWatts-Verlustkette. Clean-room (pvlib-Methodik) —
          unverbindliche Schnell-Vorbemessung (Klarhimmel-Näherung; für belastbare Zahlen
          PVGIS-Lauf bzw. Vor-Ort-Daten).
          {prefilled && (
            <span style={{ color: "var(--color-success,#16a34a)", fontWeight: 600 }}> · aus Projektdaten vorbelegt</span>
          )}
        </p>
        <div style={grid3}>
          <Field label="Breite [°]">{numInput(y, setY, "latitudeDeg")}</Field>
          <Field label="Neigung [°]">{numInput(y, setY, "tiltDeg")}</Field>
          <Field label="Azimut [°] (180 = Süd)">{numInput(y, setY, "azimuthDeg")}</Field>
          <Field label="Transpositionsmodell">
            <select
              style={inputStyle}
              value={y.transpositionModel}
              onChange={(e) => setY({ ...y, transpositionModel: e.target.value as "haydavies" | "perez" })}
            >
              <option value="perez">Perez 1990 (Standard, gegen pvlib validiert)</option>
              <option value="haydavies">Hay-Davies (klassisch)</option>
            </select>
          </Field>
        </div>
        <button className="btn-primary" style={{ marginTop: 12, fontSize: 13 }} onClick={() => void runYield()} disabled={yieldBusy}>
          {yieldBusy ? "Berechne…" : "Ertrag berechnen"}
        </button>
        {yieldRes && (
          <div style={{ marginTop: 14 }}>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
              <Kpi label="Spez. Ertrag" value={`${num(yieldRes.specificYieldKwhPerKwp, 0)} kWh/kWp`} />
              <Kpi label="POA-Einstrahlung" value={`${num(yieldRes.poaAnnualKwhM2, 0)} kWh/m²`} />
              <Kpi label="Performance Ratio" value={pct(yieldRes.performanceRatio * 100)} />
              <Kpi label="Verlustkette" value={pct(yieldRes.lossStackEfficiency * 100)} />
            </div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 90 }}>
              {yieldRes.monthlySpecificYieldKwhPerKwp.map((m, i) => (
                <div key={i} style={{ flex: 1, textAlign: "center" }}>
                  <div
                    title={`${num(m, 0)} kWh/kWp`}
                    style={{
                      height: `${(m / maxMonthly) * 70}px`,
                      background: "var(--color-primary, #2563eb)",
                      borderRadius: "3px 3px 0 0",
                    }}
                  />
                  <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginTop: 2 }}>{MONTHS[i]}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Fernverschattung (Horizont) ── */}
      <div className="card" style={card}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Fernverschattung (Horizont)</h3>
        <p style={{ fontSize: 12, color: "var(--color-text-muted)", marginBottom: 12 }}>
          Umliegende Hindernisse (Berg, Nachbargebäude, Baum) als Peilung/Distanz/Höhe eingeben →
          Jahres-Verschattungsverlust + Himmelssichtfaktor (getestete roof-core-Engine). Kann als
          zusätzlicher Verschattungsverlust in die Ertragsanalyse übernommen werden.
          {y.extraShadingPct > 0 && (
            <span style={{ color: "var(--color-warning,#d97706)", fontWeight: 600 }}> · aktiv: −{y.extraShadingPct} % im Ertrag</span>
          )}
        </p>
        {obstructions.map((o, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 8, marginBottom: 6 }}>
            <Field label={i === 0 ? "Peilung [°] (180=Süd)" : ""}>
              <input type="number" style={inputStyle} value={o.bearingDeg}
                onChange={(e) => setObstructions((arr) => arr.map((r, j) => j === i ? { ...r, bearingDeg: Number(e.target.value) } : r))} />
            </Field>
            <Field label={i === 0 ? "Distanz [m]" : ""}>
              <input type="number" style={inputStyle} value={o.distanceM}
                onChange={(e) => setObstructions((arr) => arr.map((r, j) => j === i ? { ...r, distanceM: Number(e.target.value) } : r))} />
            </Field>
            <Field label={i === 0 ? "Höhe über Auge [m]" : ""}>
              <input type="number" style={inputStyle} value={o.heightDeltaM}
                onChange={(e) => setObstructions((arr) => arr.map((r, j) => j === i ? { ...r, heightDeltaM: Number(e.target.value) } : r))} />
            </Field>
            <button type="button" title="Zeile entfernen"
              style={{ alignSelf: "end", height: 30, border: "1px solid var(--color-border,#cbd5e1)", borderRadius: 6, background: "#fff", cursor: "pointer", padding: "0 10px" }}
              onClick={() => setObstructions((arr) => arr.filter((_, j) => j !== i))}>✕</button>
          </div>
        ))}
        <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
          <button type="button" className="btn-secondary" style={{ fontSize: 13 }}
            onClick={() => setObstructions((arr) => [...arr, { bearingDeg: 180, distanceM: 20, heightDeltaM: 5 }])}>
            + Hindernis
          </button>
          <button className="btn-primary" style={{ fontSize: 13 }} onClick={() => void runHorizon()}>
            Horizont berechnen
          </button>
        </div>
        {horizonRes && (
          <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <Kpi label="Verschattungsverlust" value={pct(horizonRes.annualShadingLoss * 100)} />
            <Kpi label="Himmelssicht (SVF)" value={pct(horizonRes.skyViewFactor * 100)} />
            <button className="btn-secondary" style={{ fontSize: 13 }} onClick={applyHorizon}>
              → in Ertrag übernehmen
            </button>
          </div>
        )}
      </div>

      {/* ── Datenblatt-Import (.PAN/.OND) ── */}
      <div className="card" style={card}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Datenblatt-Import (PVsyst .PAN/.OND)</h3>
        <p style={{ fontSize: 12, color: "var(--color-text-muted)", marginBottom: 12 }}>
          PVsyst-Komponentendatei laden — Modul-/Wechselrichterwerte werden ausgelesen und können in
          den Verschaltungs-Check übernommen werden.
        </p>
        <input
          type="file"
          accept=".pan,.ond,.PAN,.OND,text/plain"
          onChange={(e) => void onSheetFile(e.target.files?.[0])}
          style={{ fontSize: 13 }}
        />
        {sheet && (
          <div style={{ marginTop: 12 }}>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Kpi label="Typ" value={sheet.kind === "pan" ? "Modul" : "Wechselrichter"} />
              {sheet.kind === "pan" && sheet.module && (
                <>
                  <Kpi label="Modell" value={sheet.module.model ?? "—"} />
                  <Kpi label="Voc" value={sheet.module.vocV ? `${num(sheet.module.vocV, 1)} V` : "—"} />
                  <Kpi label="Vmp" value={sheet.module.vmpV ? `${num(sheet.module.vmpV, 1)} V` : "—"} />
                  <Kpi label="Pnom" value={sheet.module.pnomW ? `${num(sheet.module.pnomW, 0)} Wp` : "—"} />
                </>
              )}
              {sheet.kind === "ond" && sheet.inverter && (
                <>
                  <Kpi label="Modell" value={sheet.inverter.model ?? "—"} />
                  <Kpi label="MPPT" value={`${num(sheet.inverter.vMpptMinV ?? 0, 0)}–${num(sheet.inverter.vMpptMaxV ?? 0, 0)} V`} />
                  <Kpi label="Vdc max" value={sheet.inverter.vAbsMaxV ? `${num(sheet.inverter.vAbsMaxV, 0)} V` : "—"} />
                  <Kpi label="Paco" value={sheet.inverter.pacoW ? `${num(sheet.inverter.pacoW, 0)} W` : "—"} />
                </>
              )}
            </div>
            <button className="btn-primary" style={{ marginTop: 12, fontSize: 13 }} onClick={applySheet}>
              → in Verschaltungs-Check übernehmen
            </button>
            {sheet.report && sheet.report.missingKeys.length > 0 && (
              <p style={{ fontSize: 11, color: "var(--color-warning,#d97706)", marginTop: 8 }}>
                Nicht gefunden: {sheet.report.missingKeys.join(", ")} ({sheetName})
              </p>
            )}
          </div>
        )}
      </div>

      {/* ── Verschaltungs-Check ── */}
      <div className="card" style={card}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Verschaltungs-Check (String / MPPT)</h3>
        <p style={{ fontSize: 12, color: "var(--color-text-muted)", marginBottom: 12 }}>
          Prüft String-Leerlaufspannung bei Auslegungs-Tiefsttemperatur gegen die max. DC-Spannung
          (harte Grenze) und das MPPT-Fenster bei Betriebstemperatur.
        </p>
        <div style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-muted)", marginBottom: 4 }}>Modul</div>
        {catModules.length > 0 && (
          <Field label={`Aus Katalog (${catModules.length})`}>
            <select style={inputStyle} defaultValue="" onChange={(e) => pickModule(e.target.value)}>
              <option value="">— Modul wählen (füllt Voc/Vmp/βVoc) —</option>
              {catModules.map((c) => (
                <option key={c.id} value={c.id}>{c.manufacturer} {c.modelName}</option>
              ))}
            </select>
          </Field>
        )}
        <div style={grid3}>
          <Field label="Voc STC [V]">{numInput(s, setS, "vocStc")}</Field>
          <Field label="Vmp STC [V]">{numInput(s, setS, "vmpStc")}</Field>
          <Field label="β Voc [%/K]">{numInput(s, setS, "betaVocPctPerK")}</Field>
          <Field label="Pmp STC [Wp]">{numInput(s, setS, "pmpStcW")}</Field>
        </div>
        <div style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-muted)", margin: "10px 0 4px" }}>Wechselrichter</div>
        {catInverters.length > 0 && (
          <Field label={`Aus Katalog (${catInverters.length})`}>
            <select style={inputStyle} defaultValue="" onChange={(e) => pickInverter(e.target.value)}>
              <option value="">— Wechselrichter wählen (füllt MPPT/Vdcmax/Paco) —</option>
              {catInverters.map((c) => (
                <option key={c.id} value={c.id}>{c.manufacturer} {c.modelName}</option>
              ))}
            </select>
          </Field>
        )}
        <div style={grid3}>
          <Field label="MPPT min [V]">{numInput(s, setS, "mpptMinV")}</Field>
          <Field label="MPPT max [V]">{numInput(s, setS, "mpptMaxV")}</Field>
          <Field label="Vdc max [V]">{numInput(s, setS, "vdcMaxV")}</Field>
          <Field label="MPP-Tracker">{numInput(s, setS, "mpptCount")}</Field>
          <Field label="Paco [W]">{numInput(s, setS, "pacoW")}</Field>
        </div>
        <div style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-muted)", margin: "10px 0 4px" }}>Auslegung</div>
        <div style={grid3}>
          <Field label="Module/String">{numInput(s, setS, "modulesPerString")}</Field>
          <Field label="Strings/MPPT">{numInput(s, setS, "stringsPerMppt")}</Field>
          <Field label="T min [°C]">{numInput(s, setS, "siteMinTempC")}</Field>
          <Field label="T Zelle max [°C]">{numInput(s, setS, "siteMaxCellTempC")}</Field>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          <button className="btn-primary" style={{ fontSize: 13 }} onClick={() => void runString()} disabled={strBusy}>
            {strBusy ? "Prüfe…" : "Verschaltung prüfen"}
          </button>
          <button className="btn-secondary" style={{ fontSize: 13 }} onClick={() => void runSuggest()}>
            ⚡ Optimale Stranglänge
          </button>
        </div>
        {suggestNote && (
          <p style={{ fontSize: 12, color: "var(--color-text-muted)", marginTop: 8 }}>{suggestNote}</p>
        )}
        {strRes && (
          <div style={{ marginTop: 14 }}>
            <div
              style={{
                display: "inline-block", padding: "4px 12px", borderRadius: 999, fontSize: 13, fontWeight: 700,
                background: strRes.ok ? "var(--color-success-tint, #dcfce7)" : "var(--color-error-tint, #fee2e2)",
                color: strRes.ok ? "var(--color-success, #16a34a)" : "var(--color-error, #dc2626)",
              }}
            >
              {strRes.ok ? "✓ Auslegung gültig" : "✗ Auslegung ungültig"}
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", margin: "12px 0" }}>
              <Kpi label="String Voc (kalt)" value={`${num(strRes.stringVocColdV, 0)} V`} />
              <Kpi label="String Vmp (heiß)" value={`${num(strRes.stringVmpHotV, 0)} V`} />
              <Kpi label="DC/AC-Verhältnis" value={num(strRes.dcAcRatio, 2)} />
            </div>
            {strRes.violations.length > 0 && (
              <ul style={{ margin: "6px 0", paddingLeft: 18, color: "var(--color-error, #dc2626)", fontSize: 13 }}>
                {strRes.violations.map((v, i) => <li key={i}>{v.message}</li>)}
              </ul>
            )}
            {strRes.warnings.length > 0 && (
              <ul style={{ margin: "6px 0", paddingLeft: 18, color: "var(--color-warning, #d97706)", fontSize: 13 }}>
                {strRes.warnings.map((v, i) => <li key={i}>{v.message}</li>)}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* ── Speicher-Wirtschaftlichkeit ── */}
      <div className="card" style={card}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Speicher-Wirtschaftlichkeit</h3>
        <p style={{ fontSize: 12, color: "var(--color-text-muted)", marginBottom: 12 }}>
          Eigenverbrauchs-/Batterie-Dispatch (regelbasiert) + NPV/IRR/Amortisation/LCOE.
          Unverbindlicher Richtwert.
        </p>
        <div style={grid3}>
          <Field label="Verbrauch [kWh/a]">{numInput(b, setB, "annualConsumptionKwh")}</Field>
          <Field label="Ertrag [kWh/a]">{numInput(b, setB, "annualYieldKwh")}</Field>
          <Field label="Speicher nutzbar [kWh]">{numInput(b, setB, "batteryUsableKwh")}</Field>
          <Field label="Investition [€]">{numInput(b, setB, "capexEur")}</Field>
          <Field label="Betrieb [€/a]">{numInput(b, setB, "annualOpexEur")}</Field>
          <Field label="Strompreis [€/kWh]">{numInput(b, setB, "electricityPriceEurPerKwh")}</Field>
          <Field label="Einspeisung [€/kWh]">{numInput(b, setB, "feedInTariffEurPerKwh")}</Field>
          <Field label="Zinssatz [%]">{numInput(b, setB, "discountRatePct")}</Field>
          <Field label="Laufzeit [a]">{numInput(b, setB, "years")}</Field>
        </div>
        <button className="btn-primary" style={{ marginTop: 12, fontSize: 13 }} onClick={() => void runBattery()} disabled={batBusy}>
          {batBusy ? "Berechne…" : "Wirtschaftlichkeit berechnen"}
        </button>
        {batRes && (
          <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
            <Kpi label="Eigenverbrauch (SCR)" value={pct(batRes.dispatch.selfConsumptionRate * 100)} />
            <Kpi label="Autarkie (SSR)" value={pct(batRes.dispatch.selfSufficiencyRate * 100)} />
            <Kpi label="Ersparnis/Jahr" value={eur(batRes.economics.annualSavingsEur)} />
            <Kpi label="NPV" value={eur(batRes.economics.npvEur)} />
            <Kpi label="IRR" value={batRes.economics.irr === null ? "—" : pct(batRes.economics.irr * 100)} />
            <Kpi label="Amortisation" value={batRes.economics.paybackYears === null ? "> Laufzeit" : `${batRes.economics.paybackYears} a`} />
            <Kpi label="LCOE (real)" value={`${num(batRes.economics.realLcoeEurPerKwh, 3)} €/kWh`} />
          </div>
        )}
      </div>
    </div>
  );
}
